import { motion } from "framer-motion";
import { FaBible, FaBook, FaBookOpen, FaChurch, FaCross, FaHandsHelping, FaHeart, FaPrayingHands } from "react-icons/fa";
import image1 from "../images/329081140_1713902169003588_4496020940095923112_n.jpg";
import image2 from "../images/Prophet.jpeg";
import PageHeader from "./common/PageHeader";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 10
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.8 }
  }
};

const About = () => {
  return (
    <motion.main 
      className="pt-5 space-y-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <PageHeader 
        title="About Us" 
        subtitle="Get to know our ministry"
        imageUrl="https://images.unsplash.com/photo-1455849318743-b2233052fcff?q=80&w=2069&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        imageAlt="Church Events"
      />

      {/* About the Ministry Section */}
      <motion.section 
        className="container pt-10 mx-auto flex flex-col sm:flex-row gap-6 px-4"
        variants={itemVariants}
      >
        <motion.div 
          className="flex-1 space-y-4"
          variants={itemVariants}
        >
          <motion.h1 
            className="text-2xl font-schoolbook italic text-primary"
            variants={itemVariants}
          >
            About the ministry
          </motion.h1>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            Excellent Love Ministries is a place of hope, love, and transformation — dedicated to raising future leaders. It was founded in 2015 in the village of Shongoane, Lephalale, by Prophet P.R. Molaba and I.R. Molaba. The ministry began with home-cells in various households, and by God's grace, we held our first Sunday service on May 31, 2015.
          </motion.p>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            Today, we have grown to six branches located in Lephalale, Mokopane, Moletši, Gauteng, Lebowakgomo, and Burgersfort. In addition, we have active home-cells in several areas across Botswana.
          </motion.p>
        </motion.div>
        <motion.img
          className="flex-1 h-64 sm:h-80 w-full object-cover rounded-lg shadow-lg"
          src={image1}
          alt="Excellent Love Ministries"
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
        />
      </motion.section>

      {/* Mission and Values Section */}
      <motion.section 
        className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 px-4"
        variants={containerVariants}
      >
        {/* Vision Card */}
        <motion.div 
          className="bg-white p-6 shadow-lg rounded-lg border-l-4 border-primary"
          variants={itemVariants}
          whileHover={{ y: -5 }}
        >
          <div className="flex items-center mb-4">
            <FaChurch className="text-primary text-2xl mr-3" />
            <h1 className="text-xl font-schoolbook italic text-primary">Our Vision</h1>
          </div>
          <p className="text-gray-700">
            To bring love, hope, and faith to all people (1 Corinthians 13:13), and to prepare disciples for the coming of Christ through sound teaching of God's Word.
          </p>
        </motion.div>

        {/* Mission Card */}
        <motion.div 
          className="bg-white p-6 shadow-lg rounded-lg border-l-4 border-primary"
          variants={itemVariants}
          whileHover={{ y: -5 }}
        >
          <div className="flex items-center mb-4">
            <FaCross className="text-primary text-2xl mr-3" />
            <h1 className="text-xl font-schoolbook italic text-primary">Our Mission</h1>
          </div>
          <ul className="text-gray-700 space-y-3">
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
                <FaHandsHelping size={14} />
              </span>
              To save and win souls for the Kingdom of God through crusades, evangelism, and home-cells.
            </li>
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
                <FaBook size={14} />
              </span>
              To transform lives through the Word of God and restore hope to the brokenhearted.
            </li>
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
                <FaHeart size={14} />
              </span>
              To support the less privileged, orphans, and widows.
            </li>
          </ul>
          <div className="mt-4 text-sm text-primary font-medium">
            "He that hath pity upon the poor lendeth unto the LORD" — Proverbs 19:17
          </div>
        </motion.div>

        {/* Values Card */}
        <motion.div 
          className="bg-white p-6 shadow-lg rounded-lg border-l-4 border-primary"
          variants={itemVariants}
          whileHover={{ y: -5 }}
        >
          <div className="flex items-center mb-4">
            <FaPrayingHands className="text-primary text-2xl mr-3" />
            <h1 className="text-xl font-schoolbook italic text-primary">Our Values</h1>
          </div>
          <ul className="text-gray-700 space-y-3">
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
               <FaHeart size={14} />
              </span>
              <div>
                <strong>Love</strong>
                <p className="text-sm">We love all people, just as Christ loves us.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
                <FaBible size={14} />
              </span>
              <div>
                <strong>The Holy Spirit</strong>
                <p className="text-sm">We are led by the Spirit of God in all that we do.</p>
              </div>
            </li>
            <li className="flex items-start">
              <span className="inline-block bg-primary/10 text-primary rounded-full p-1 mr-2">
                <FaBookOpen size={14} />
              </span>
              <div>
                <strong>The Word and Prayer</strong>
                <p className="text-sm">God's Word is our foundation, and prayer is our lifestyle.</p>
              </div>
            </li>
          </ul>
        </motion.div>
      </motion.section>

      {/* About Founder Section */}
      <motion.section 
        className="container mx-auto flex flex-col sm:flex-row gap-8 px-4 py-10"
        variants={containerVariants}
      >
        <motion.div 
          className="flex-1 relative"
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
        >
          <img 
            className="w-full h-full max-h-[500px] object-cover rounded-lg shadow-xl"
            src={image2} 
            alt="Prophet PR Molaba" 
          />
          <motion.div 
            className="absolute -bottom-4 -right-4 bg-primary text-white px-6 py-2 rounded-lg shadow-lg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <span className="font-bold">Prophet P.R. Molaba</span>
          </motion.div>
        </motion.div>
        <motion.div 
          className="flex-1 space-y-6"
          variants={itemVariants}
        >
          <motion.h1 
            className="text-3xl font-schoolbook italic text-primary"
            variants={itemVariants}
          >
            About Our Founder
          </motion.h1>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            Prophet PR Molaba was born in Ga-Mphahlele village, seleteng in Limpopo province. He is the first born in the family of brothers and sisters. He started school there until he finished matric.
          </motion.p>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            He went on to further his studies and work as an electrician. His late father was a pastor. He grew up in a faith based family that loved God. The call of God came while he was working then God directed him to Apostle EC Mphela to serve there.
          </motion.p>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            During his service the Lord directed him of the lady he should marry because of the work of the ministry. Prophetess I R Molaba was born in Ga-Mphahlele, seleteng, Limpopo province. She is the last born of the family. They got married in 17&18 September 2015. They are blessed with 2 kids.
          </motion.p>
          <motion.p 
            className="text-gray-700 leading-relaxed"
            variants={itemVariants}
          >
            Currently the headquarters of ELM is in lephalale. Prophet is passionate about going crusade, saving the lost souls for Jesus and teaching the true gospel of Jesus to equip them to be strong believers.
          </motion.p>
        </motion.div>
      </motion.section>
    </motion.main>
  );
};

export default About;